import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Player } from '@/types';
import { playerRepo } from '@/db/repositories/playerRepo';

interface PlayerState {
  players: Player[];
  isLoading: boolean;
  error: string | null;
}

interface PlayerActions {
  loadPlayers: () => Promise<void>;
  addPlayer: (name: string) => Promise<Player | null>;
  updatePlayer: (id: string, name: string) => Promise<boolean>;
  deletePlayer: (id: string) => Promise<boolean>;
  getPlayerById: (id: string) => Player | undefined;
  clearError: () => void;
}

type PlayerStore = PlayerState & PlayerActions;

export const usePlayerStore = create<PlayerStore>()(
  persist(
    immer((set, get) => ({
      // State
      players: [],
      isLoading: false,
      error: null,

      // Actions
      loadPlayers: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const players = await playerRepo.getAll();
          set((state) => {
            state.players = players;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to load players';
            state.isLoading = false;
          });
        }
      },

      addPlayer: async (name: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          // Check for duplicate name
          const exists = await playerRepo.nameExists(name);
          if (exists) {
            set((state) => {
              state.error = 'A player with this name already exists';
            });
            return null;
          }

          const player = await playerRepo.create(name);
          set((state) => {
            state.players.push(player);
          });
          return player;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to add player';
          });
          return null;
        }
      },

      updatePlayer: async (id: string, name: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          // Check for duplicate name
          const exists = await playerRepo.nameExists(name, id);
          if (exists) {
            set((state) => {
              state.error = 'A player with this name already exists';
            });
            return false;
          }

          const updated = await playerRepo.update(id, name);
          if (updated) {
            set((state) => {
              const index = state.players.findIndex((p) => p.id === id);
              if (index !== -1) {
                state.players[index] = updated;
              }
            });
            return true;
          }
          return false;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to update player';
          });
          return false;
        }
      },

      deletePlayer: async (id: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          const deleted = await playerRepo.delete(id);
          if (deleted) {
            set((state) => {
              state.players = state.players.filter((p) => p.id !== id);
            });
            return true;
          }
          return false;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to delete player';
          });
          return false;
        }
      },

      getPlayerById: (id: string) => {
        return get().players.find((p) => p.id === id);
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
    })),
    {
      name: 'belot-players',
      partialize: (state) => ({ players: state.players }),
    }
  )
);
